/**
 * Challenge Badges
 *
 * A badge is minted the first time a user completes a (non-repeatable)
 * challenge. Badge IDs are `badge-<challengeId>`. Badges are pure prestige and
 * appear in the profile Badge Case, grouped into progressive columns by the
 * challenge's category. See CHALLENGES_SYSTEM.md §3.2. Emoji are real glyphs.
 */

import type { Rarity } from './rarity';
import { COURSE_SECTION_TRACKS } from './challenges';

export interface Badge {
  id: string;
  name: string;
  emoji: string;
  rarity: Rarity;
  blurb: string;
}

function b(id: string, name: string, emoji: string, rarity: Rarity, blurb: string): Badge {
  return { id: `badge-${id}`, name, emoji, rarity, blurb };
}

/** Course icon per section track, keyed by the challenge category. */
const COURSE_ICONS: Record<string, string> = {
  'course-basics': '📝',
  'course-python': '🐍',
  'course-hebrew': '🇮🇱',
  'course-russian': '🇷🇺',
};

export const BADGES: Badge[] = [
  // Speed
  b('wpm-30', 'Warm-Up', '⚡', 'common', 'Found your rhythm.'),
  b('wpm-40', 'Quick Fingers', '🤙', 'common', 'Picking up speed.'),
  b('wpm-55', 'Nimble', '💨', 'rare', 'Quick on the keys.'),
  b('wpm-70', 'Fast Hands', '🌬️', 'rare', 'Seriously fast.'),
  b('wpm-85', 'Blazing', '🔥', 'epic', 'On fire.'),
  b('wpm-100', 'Speed Demon', '😈', 'legendary', 'Triple-digit typing.'),
  // Volume
  b('vol-10', 'First Steps', '🌱', 'common', 'Just getting going.'),
  b('vol-25', 'Getting Started', '🌿', 'common', 'Building momentum.'),
  b('vol-50', 'Bookworm', '📘', 'rare', 'Always practising.'),
  b('vol-100', 'Centurion', '💯', 'rare', 'A hundred levels deep.'),
  b('vol-250', 'Veteran', '🎖️', 'epic', 'Seasoned typist.'),
  b('vol-1000', 'Grandmaster', '🏆', 'legendary', 'A thousand levels.'),
  // Accuracy
  b('acc-5', 'Sharpshooter', '🏹', 'common', 'Dead-on accuracy.'),
  b('acc-10', 'Flawless', '🎯', 'rare', 'Ten perfect runs.'),
  b('acc-25', 'Precise', '📍', 'epic', 'Surgical precision.'),
  b('acc-50', 'Immaculate', '💎', 'epic', 'Spotless typing.'),
  b('acc-100', 'Perfectionist', '👑', 'legendary', 'A hundred flawless levels.'),
  // Coding (student track)
  b('code-5', 'Code Cadet', '📖', 'common', 'First lines down.'),
  b('code-10', 'Code Student', '🧑‍💻', 'rare', 'Learning the syntax.'),
  b('code-25', 'Code Adept', '💻', 'epic', 'Fluent in code.'),
  b('code-50', 'Code Scholar', '🦉', 'epic', 'Deep in the docs.'),
  b('code-100', 'Code Master', '🎓', 'legendary', 'A hundred code levels.'),
  // Multilingual
  b('ml-2', 'Bilingual', '🗣️', 'common', 'Two languages in.'),
  b('ml-3', 'Trilingual', '💬', 'rare', 'Three and counting.'),
  b('ml-4', 'Polyglot', '🌐', 'epic', 'Four languages practised.'),
  b('ml-5', 'Hyperpolyglot', '🌍', 'legendary', 'Five languages mastered.'),
  // Multi-keyboard
  b('mk-2', 'Two-Key Typist', '⌨️', 'common', 'Two keyboards in.'),
  b('mk-3', 'Keyboard Hopper', '🎹', 'rare', 'Three layouts deep.'),
  b('mk-4', 'Layout Nomad', '🧩', 'epic', 'Four keyboards practised.'),
  b('mk-5', 'Keyboard Polyglot', '🗝️', 'legendary', 'Five keyboards mastered.'),
  // Mastery
  b('mas-5', 'Rising Star', '⭐', 'common', 'Five-star starter.'),
  b('mas-10', 'Star Student', '🌟', 'rare', 'Shining bright.'),
  b('mas-25', 'Stellar', '✨', 'epic', 'Top marks across the board.'),
  b('mas-50', 'Constellation', '💫', 'epic', 'A whole sky of stars.'),
  b('mas-100', 'Superstar', '🌠', 'legendary', 'Mastery, perfected.'),
  // Consistency
  b('streak-3', 'Habit Forming', '📆', 'common', 'Three days running.'),
  b('streak-5', 'Consistent', '📅', 'common', 'Five-day streak.'),
  b('streak-7', 'Week Warrior', '🗓️', 'rare', 'A full week.'),
  b('streak-14', 'Committed', '🔁', 'rare', 'Two weeks strong.'),
  b('streak-30', 'On Fire', '🌋', 'epic', 'A month unbroken.'),
  b('streak-100', 'Iron Will', '🏔️', 'legendary', 'A hundred days straight.'),
  // Dedication
  b('ded-10', 'Regular', '👣', 'common', 'Ten days in.'),
  b('ded-30', 'Devoted', '💪', 'rare', 'Thirty days of practice.'),
  b('ded-60', 'Persistent', '🧗', 'epic', 'Sixty days logged.'),
  b('ded-120', 'Relentless', '🦾', 'epic', 'Never lets up.'),
  b('ded-365', 'Year-Rounder', '🎓', 'legendary', 'A year of practice days.'),
  // Course sections — one prestige badge per section, grouped by course.
  ...sectionBadges(),
];

/** Rarity escalates through a course; the final section is the legendary capstone. */
function sectionRarity(index: number, count: number): Rarity {
  if (index === count - 1) return 'legendary';
  const pct = (index + 1) / count;
  if (pct <= 0.34) return 'common';
  if (pct <= 0.67) return 'rare';
  return 'epic';
}

/**
 * Section badges, derived from the same COURSE_SECTION_TRACKS the challenges use
 * so IDs (`badge-sec-<sectionId>`) and ordering stay in lock-step.
 */
function sectionBadges(): Badge[] {
  const out: Badge[] = [];
  for (const track of COURSE_SECTION_TRACKS) {
    const icon = COURSE_ICONS[track.category] ?? '📚';
    track.metas.forEach((meta, i) => {
      out.push(
        b(`sec-${meta.id}`, `${track.label} · ${meta.title}`, icon, sectionRarity(i, track.metas.length), meta.description),
      );
    });
  }
  return out;
}

export function getBadge(id: string | undefined): Badge | null {
  if (!id) return null;
  return BADGES.find(b => b.id === id) ?? null;
}
